import React, { useState, useEffect } from 'react';
import { allowlistService } from '../services/allowlistService';
import { useAuth } from '../context/AuthContext';
import './AllowlistManager.css';
import Modal from './Modal';

// AllowlistForm component for adding/editing allowlist entries
const AllowlistForm = ({ entry, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    userType: 'user',
    ...(entry || {}),
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Basic validation
      if (!formData.email) {
        throw new Error('Email is required');
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Invalid email format');
      }

      // Save the allowlist entry
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save allowlist entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="allowlist-form" onSubmit={handleSubmit}>
      <h3>{entry ? 'Edit Allowlist Entry' : 'Add New Allowlist Entry'}</h3>

      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="user@example.com"
          required
          disabled={entry} // Disable email editing for existing entries
        />
      </div>

      <div className="form-group">
        <label htmlFor="userType">User Type</label>
        <select
          id="userType"
          name="userType"
          value={formData.userType}
          onChange={handleChange}
          required
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="form-button cancel-button"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="form-button save-button"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

// Main AllowlistManager component
const AllowlistManager = () => {
  const { user } = useAuth();
  const [allowlist, setAllowlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('info');
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalConfirmAction, setModalConfirmAction] = useState(null);
  const [modalConfirmText, setModalConfirmText] = useState('OK');
  const [modalCancelText, setModalCancelText] = useState('');

  const fetchAllowlist = async () => {
    setLoading(true);
    try {
      const data = await allowlistService.getAllAllowlistEntries();
      setAllowlist(data);
      setError('');
    } catch (err) {
      setError('Failed to load allowlist entries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllowlist();
  }, []);

  const handleSaveEntry = async (entryData) => {
    try {
      if (editingEntry) {
        // Update existing entry
        await allowlistService.updateAllowlistEntry(editingEntry._id, entryData);
      } else {
        // Create new entry
        await allowlistService.addAllowlistEntry(entryData);
      }

      // Refresh the list
      fetchAllowlist();

      // Close form
      setEditingEntry(null);
      setIsAddingNew(false);
    } catch (err) {
      console.error('Error saving allowlist entry:', err);
      throw err;
    }
  };

  const handleDeleteEntry = async (id) => {
    showConfirmModal(
      'Delete Allowlist Entry',
      'Are you sure you want to delete this allowlist entry?',
      async () => {
        try {
          await allowlistService.deleteAllowlistEntry(id);
          fetchAllowlist();
          showInfoModal('Success', 'Allowlist entry deleted successfully');
        } catch (err) {
          setError('Failed to delete allowlist entry');
          showErrorModal('Error', 'Failed to delete allowlist entry');
          console.error(err);
        }
      },
      'Delete',
      'Cancel',
      'error'
    );
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setIsAddingNew(false);
  };

  // Helper functions for modals
  const showInfoModal = (title, message) => {
    setModalType('info');
    setModalTitle(title);
    setModalMessage(message);
    setModalConfirmAction(null);
    setModalConfirmText('OK');
    setModalCancelText('');
    setModalOpen(true);
  };

  const showErrorModal = (title, message) => {
    setModalType('error');
    setModalTitle(title);
    setModalMessage(message);
    setModalConfirmAction(null);
    setModalConfirmText('OK');
    setModalCancelText('');
    setModalOpen(true);
  };

  const showConfirmModal = (title, message, confirmAction, confirmText = 'Confirm', cancelText = 'Cancel', type = 'warning') => {
    setModalType(type);
    setModalTitle(title);
    setModalMessage(message);
    setModalConfirmAction(() => confirmAction);
    setModalConfirmText(confirmText);
    setModalCancelText(cancelText);
    setModalOpen(true);
  };

  // Filter allowlist based on search term
  const filteredAllowlist = allowlist.filter(entry => {
    const term = searchTerm.toLowerCase();
    return entry.email.toLowerCase().includes(term);
  });

  // Check if user is admin
  if (!user || user.userType !== 'admin') {
    return (
      <div className="access-denied">
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="allowlist-manager">
      <h2>Allowlist Management</h2>

      <div className="allowlist-manager-top-bar">
        <div className="allowlist-manager-actions">
          <button
            onClick={() => {
              setEditingEntry(null);
              setIsAddingNew(true);
            }}
            disabled={isAddingNew || editingEntry}
            className="action-button"
          >
            Add New Email
          </button>
        </div>
        <div className="allowlist-manager-search">
          <input
            type="search"
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isAddingNew && (
        <AllowlistForm
          onSave={handleSaveEntry}
          onCancel={handleCancelEdit}
        />
      )}

      {editingEntry && (
        <AllowlistForm
          entry={editingEntry}
          onSave={handleSaveEntry}
          onCancel={handleCancelEdit}
        />
      )}

      {loading ? (
        <div className="loading">Loading allowlist entries...</div>
      ) : filteredAllowlist.length === 0 ? (
        <div className="no-entries">
          <p>{searchTerm ? 'No entries match your search.' : 'No allowlist entries found. Add your first entry using the button above.'}</p>
        </div>
      ) : (
        <div className="allowlist-list">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>User Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAllowlist.map((entry) => (
                <tr key={entry._id}>
                  <td>{entry.email}</td>
                  <td>{entry.userType}</td>
                  <td>
                    <div className="entry-actions">
                      <button onClick={() => setEditingEntry(entry)}>Edit</button>
                      <button onClick={() => handleDeleteEntry(entry._id)} className="delete">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal component */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        confirmText={modalConfirmText}
        cancelText={modalCancelText}
        onConfirm={modalConfirmAction}
        type={modalType}
      />
    </div>
  );
};

export default AllowlistManager;
